import { useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import PolicyCTA from "@/components/policy/PolicyCTA";
import PolicyHero from "@/components/policy/PolicyHero";
import RevealStyles from "@/components/policy/RevealStyles";
import {
  GraduationCap,
  CheckCircle,
  XCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Users,
  DollarSign,
  Shield,
  Heart,
  Eye,
  Scale,
  BookOpen,
  Bus,
  AlertTriangle,
  FileText,
  Clock,
  TrendingUp,
  Building2,
  Search,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-white/[0.03]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors">
        <span className="font-bold text-white pr-4">{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/30 flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5"><p className="text-white/50 leading-relaxed">{answer}</p></div>}
    </div>
  );
};

const publicSchoolDuties = [
  "Must take every child",
  "Must provide transportation",
  "Must provide special education",
  "Must provide speech therapy",
  "Must provide ESL support",
  "Must provide behavioral support",
  "Must provide athletics & extracurriculars",
  "Must educate students regardless of academic performance",
  "Must serve high-needs students",
  "Expected to maintain fully licensed teaching staff",
  "Must absorb the highest-cost student populations",
];

const charterRealities = [
  { text: "Receive public taxpayer funding", check: true },
  { text: "May not provide transportation", check: false },
  { text: "May not provide the same extracurriculars", check: false },
  { text: "May not provide the same support infrastructure", check: false },
  { text: "May use application-based enrollment systems", check: false },
  { text: "May naturally enroll fewer high-needs students", check: false },
  { text: "NV law allows up to 20% of teachers to be unlicensed in certain charter settings", check: false },
  { text: "Often serve different student populations than neighborhood public schools", check: false },
];

const fundingAdjustments = [
  { service: "No transportation provided", icon: Bus },
  { service: "No athletics or extracurriculars", icon: Users },
  { service: "No speech therapy services", icon: Heart },
  { service: "No behavioral support services", icon: Shield },
  { service: "No ESL support programs", icon: BookOpen },
  { service: "No full special education capacity", icon: GraduationCap },
];

const timeline = [
  { year: "2002", title: "No Child Left Behind Signed", desc: "President George W. Bush signs NCLB, introducing standardized testing mandates and Adequate Yearly Progress (AYP) requirements for all public schools" },
  { year: "2003–08", title: "Rise of Accountability Systems", desc: "Schools rated and ranked based on test scores. Schools serving high-poverty, high-needs populations disproportionately labeled as 'failing'" },
  { year: "2005–10", title: "Charter School Expansion", desc: "Charter enrollment grows significantly nationwide as 'school choice' is promoted as an alternative to 'failing' public schools" },
  { year: "2010–15", title: "School Choice Movement Grows", desc: "Voucher programs, tax-credit scholarships, and charter school networks expand across multiple states" },
  { year: "2015–Now", title: "Continuing Public School Underfunding", desc: "Despite growing student populations and increasing needs, many public school systems face persistent funding challenges" },
];

const faqs = [
  { question: "Is Cooper against charter schools?", answer: "No. Cooper supports good schools of all types. This is not about banning charter schools — it is about ensuring that every school receiving public taxpayer funding carries the same public responsibilities. Equal funding should require equal obligations." },
  { question: "Does this mean charter schools would be shut down?", answer: "No. This means charter schools receiving full public funding would be expected to provide the same services as neighborhood public schools — including transportation, special education, ESL support, behavioral services, and extracurriculars. Schools that do not provide equivalent services would receive adjusted funding reflecting the services they actually deliver." },
  { question: "Aren't charter schools better than public schools?", answer: "Research shows mixed results. Some charter schools perform well, while others do not outperform comparable public schools. However, when charter schools serve different student populations — fewer special needs students, fewer ESL students, fewer high-needs students — direct performance comparisons are misleading. Different responsibilities create different conditions." },
  { question: "What about parent choice?", answer: "Cooper supports parental involvement and educational choice. But taxpayer-funded choice must come with taxpayer-funded accountability. Parents should have options — and every option funded by public money should be held to the same standards of service, transparency, and responsibility." },
  { question: "What does 'service-adjusted funding' mean?", answer: "It means that funding levels would reflect the services a school actually provides. A school that provides transportation, special education, ESL support, behavioral services, and full extracurriculars would receive full funding. A school that does not provide some of these services would receive funding adjusted to reflect that — because taxpayers should not pay for services that are not being delivered." },
  { question: "Won't this hurt students in charter schools?", answer: "This policy protects all students by ensuring that every publicly funded school provides the services students need. Students in charter schools deserve the same services that students in public schools receive. If a school receives public money, it should deliver public services." },
];

const sources = [
  { title: "Nevada Pupil-Centered Funding Plan", desc: "Nevada's school funding formula and per-pupil allocation methodology" },
  { title: "Nevada Revised Statutes — Charter Schools", desc: "NRS 388A governing charter school operations, teacher licensing, and obligations" },
  { title: "No Child Left Behind Act of 2001 (P.L. 107-110)", desc: "Federal legislation establishing testing mandates and accountability systems" },
  { title: "RAND Corporation — Charter School Research", desc: "Comprehensive studies on charter school performance and demographic differences" },
  { title: "Brookings Institution — Education Policy", desc: "Research on school choice, accountability systems, and educational equity" },
  { title: "U.S. Department of Education", desc: "Federal data on school performance, demographics, and funding" },
  { title: "National Center for Education Statistics (NCES)", desc: "Student demographic data, enrollment patterns, and school characteristics" },
  { title: "Stanford CREDO — Charter School Studies", desc: "Large-scale studies comparing charter and public school outcomes" },
];

const EducationPage = () => {
  useReveal();

  return (
    <div data-testid="education-page" className="bg-[#0B1A3B]">
      {/* ── HERO ── */}
      <PolicyHero
        testId="education-hero"
        badge={{ icon: GraduationCap, label: "Education Policy" }}
        headline={
          <>
            Equal Funding Requires{" "}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
              Equal Responsibility
            </span>
          </>
        }
      >
        <p className="text-xl sm:text-2xl text-white/50 max-w-2xl mx-auto mb-6">
          Nevada taxpayers should not pay full-service funding for partial-service schools.
        </p>

        <div className="bg-white/[0.04] border border-white/10 p-6 max-w-2xl mx-auto mb-10 backdrop-blur-sm">
          <p className="text-white/70 leading-relaxed italic">
            "I support good schools of all types. But if a school receives the same public funding as a neighborhood public school, it should accept the same public responsibilities."
          </p>
          <p className="text-[#D4AF37] font-bold text-sm mt-3">— Cooper</p>
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <a href="#the-comparison" className="group inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.35)]">
            Read the Plan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a href="#sources" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all">
            See the Research
          </a>
          <Link to="/volunteer" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all">
            Support Public Schools
          </Link>
        </div>
      </PolicyHero>

      {/* ── THE COMPARISON ── */}
      <section id="the-comparison" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Reality</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              Wait… These Schools Receive the Same Funding?
            </h2>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-6 mb-10">
            {/* Public Schools */}
            <div className="bg-emerald-500/[0.06] border-2 border-emerald-500/20 p-6">
              <h3 className="text-emerald-400 font-bold text-lg mb-5">Neighborhood Public Schools</h3>
              <div className="space-y-3">
                {publicSchoolDuties.map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-white/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Charter Models */}
            <div className="bg-white/[0.04] border-2 border-white/10 p-6">
              <h3 className="text-white/80 font-bold text-lg mb-5">Some Charter School Models</h3>
              <div className="space-y-3">
                {charterRealities.map((item) => (
                  <div key={item.text} className="flex items-start gap-2">
                    {item.check ? (
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-white/60 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="reveal bg-white/[0.04] border-2 border-[#D4AF37]/40 p-8 text-center mb-6">
            <p className="text-2xl sm:text-3xl font-black text-white leading-tight">
              "Taxpayers should not pay full-service funding for{" "}
              <span className="text-[#D4AF37]">partial-service schools</span>."
            </p>
          </div>
          <div className="reveal text-center">
            <p className="text-white/50 text-lg">
              Neighborhood public schools take everyone. That is why they require more resources.
            </p>
          </div>
        </div>
      </section>

      {/* ── EQUAL FUNDING = EQUAL RESPONSIBILITY ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <Scale className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Framework</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
              Equal Funding Requires Equal Responsibility
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto text-lg">
              "If a school wants full public funding, it should provide full public services."
            </p>
          </div>

          <div className="reveal">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/30 mb-6 text-center">Service-Adjusted Funding Model</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {fundingAdjustments.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.service} className="bg-white/[0.04] border border-white/10 p-5">
                    <Icon className="w-6 h-6 text-red-400 mb-3" />
                    <p className="text-white/70 text-sm font-medium mb-2">{item.service}</p>
                    <p className="text-red-400 text-xs font-bold uppercase tracking-wider">= Adjusted Funding</p>
                  </div>
                );
              })}
            </div>
            <p className="text-white/30 text-xs text-center italic mb-8">
              Note: Specific percentages are illustrative examples pending legislative review and cost analysis.
            </p>
          </div>

          <div className="reveal bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 p-8 text-center">
            <p className="text-2xl font-black text-white">
              "Public money must come with{" "}
              <span className="text-[#D4AF37]">public responsibility</span>."
            </p>
          </div>
        </div>
      </section>

      {/* ── CORE ARGUMENT ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-8">
              This Is About Fairness
            </h2>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {[
                "This is NOT about attacking families",
                "This is NOT about attacking students",
                "This is NOT about banning charter schools",
                "This IS about fairness, accountability, and taxpayer value",
              ].map((item) => (
                <div key={item} className={`flex items-start gap-3 p-4 ${item.includes("NOT") ? "bg-red-500/[0.06] border border-red-500/20" : "bg-emerald-500/[0.06] border border-emerald-500/20"}`}>
                  {item.includes("NOT") ? <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" /> : <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />}
                  <span className="text-white/70 text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-white/50 leading-relaxed mb-4">
              Neighborhood public schools educate every child who arrives — special needs students, children with behavioral challenges, children learning English, children with trauma, struggling students, low-income students, average students, gifted students. Every single one.
            </p>
            <p className="text-white/50 leading-relaxed mb-8">
              If another publicly funded school system wants the same taxpayer funding, it should carry the same taxpayer responsibilities.
            </p>

            <div className="bg-white/[0.04] border-2 border-[#D4AF37]/30 p-8">
              <p className="text-white font-bold text-lg leading-relaxed">
                "Public schools are not failing because children changed. They are struggling because politicians demanded more, funded less, punished hardship, and then called privatization{" "}
                <span className="text-[#D4AF37]">the solution</span>."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE INCENTIVE PROBLEM ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Hidden Problem</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-8">
              The Incentive Problem
            </h2>

            <div className="space-y-4 mb-8">
              {[
                { icon: Bus, text: "A school without buses naturally becomes less accessible to low-income families" },
                { icon: Heart, text: "A school without robust special education infrastructure naturally enrolls fewer high-needs students" },
                { icon: FileText, text: "Application systems, parent participation requirements, and disciplinary structures can shape who actually attends" },
                { icon: Search, text: "Counseling-out practices and support limitations further filter student populations" },
                { icon: Users, text: "Meanwhile, neighborhood public schools must take everyone who arrives — no exceptions" },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.text} className="flex items-start gap-4 bg-white/[0.04] border border-white/10 p-5">
                    <Icon className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/60 text-sm leading-relaxed">{item.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="bg-white/[0.04] border-2 border-[#D4AF37]/40 p-8 text-center">
              <p className="text-xl sm:text-2xl font-black text-white">
                "Some schools are being judged as better while operating under{" "}
                <span className="text-[#D4AF37]">different responsibilities</span>."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── TEACHER STANDARDS ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37]"><BookOpen className="w-6 h-6" /></div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Teacher Standards</h2>
                <p className="text-white/40 mt-2">Every publicly funded classroom should have licensed educators</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-emerald-500/[0.06] border border-emerald-500/20 p-6">
                <h3 className="text-emerald-400 font-bold mb-3">Public Schools</h3>
                <p className="text-white/60 text-sm mb-3">Effectively operate under strict licensing expectations for all teaching staff.</p>
                <div className="bg-emerald-500/10 p-3"><p className="text-emerald-400 text-2xl font-black text-center">100%</p><p className="text-emerald-400/60 text-xs text-center">Licensed Teacher Expectation</p></div>
              </div>
              <div className="bg-white/[0.04] border border-white/10 p-6">
                <h3 className="text-white/80 font-bold mb-3">Certain Charter Settings</h3>
                <p className="text-white/60 text-sm mb-3">Nevada law allows up to 20% of teachers to be unlicensed in certain charter school settings.</p>
                <div className="bg-white/5 p-3"><p className="text-white text-2xl font-black text-center">80%</p><p className="text-white/40 text-xs text-center">Minimum Licensed Requirement</p></div>
              </div>
            </div>
            <p className="text-white/30 text-xs italic">Source: Nevada Revised Statutes — Charter School Regulations (NRS 388A)</p>
          </div>
        </div>
      </section>

      {/* ── ENROLLMENT TRANSPARENCY ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37]"><Eye className="w-6 h-6" /></div>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Enrollment Fairness & Transparency</h2>
            </div>

            <p className="text-white/50 leading-relaxed mb-6">
              Cooper proposes audits comparing charter school demographics to nearby public schools, along with full transparency around enrollment patterns, support services, and disciplinary practices.
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Special education enrollment rates",
                "ESL enrollment rates",
                "Student withdrawals and transfers",
                "Suspensions and expulsions",
                "Disciplinary removals",
                "Counseling-out practices",
                "Behavioral support availability",
                "Demographic comparisons with nearby public schools",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 bg-white/[0.04] border border-white/10 p-3">
                  <Search className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-white/60 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── NCLB & PRIVATIZATION TIMELINE ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Historical Context</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
              No Child Left Behind & the Privatization Pipeline
            </h2>
            <p className="text-white/50 leading-relaxed mb-10">
              Critics argue that accountability systems introduced under No Child Left Behind disproportionately harmed high-poverty schools, punishing schools for serving the students who needed the most help — and that this framework became justification for privatization-oriented reforms.
            </p>

            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-px bg-[#D4AF37]/20" />
              {timeline.map((item) => (
                <div key={item.year} className="relative flex items-start gap-6 mb-8 last:mb-0">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black text-xs font-black z-10">
                    {item.year.substring(0, 4)}
                  </div>
                  <div className="pt-2">
                    <h3 className="text-white font-bold mb-1">{item.title}</h3>
                    <p className="text-white/40 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-white/30 text-xs mt-8 italic">
              Sources: George W. Bush Presidential Library; U.S. Department of Education; RAND Corporation; Brookings Institution
            </p>
          </div>
        </div>
      </section>

      {/* ── WHAT COOPER SUPPORTS ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <Shield className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">What Cooper Supports</h2>
          </div>

          <div className="reveal grid sm:grid-cols-2 gap-4 mb-10">
            {[
              "Good schools of all types",
              "Parental involvement in education",
              "Educational innovation and excellence",
              "Licensed, well-compensated teachers",
              "Full transparency and accountability",
              "Equal obligations for equal funding",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-emerald-500/[0.06] border border-emerald-500/20 p-4">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <span className="text-white/70 text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>

          <div className="reveal bg-white/[0.04] border-2 border-[#D4AF37]/30 p-8 text-center">
            <p className="text-white font-bold text-lg">
              "This is not about attacking charter schools. It is about protecting{" "}
              <span className="text-[#D4AF37]">every child and every taxpayer</span>."
            </p>
          </div>
        </div>
      </section>

      {/* ── THE BIGGER VISION ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center">
            <Heart className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-8">The Bigger Vision</h2>
            <div className="space-y-4 text-white/50 text-lg leading-relaxed">
              <p>One unified Nevada education system. No two-tier system. No separating children by income, disability, language ability, or learning needs.</p>
              <p>Restoring investment in neighborhood schools. Respecting teachers. Supporting parents. Giving every child opportunity.</p>
            </div>

            <div className="mt-10 bg-white/[0.04] border-2 border-[#D4AF37]/40 p-8">
              <p className="text-xl font-black text-white mb-4">
                "Neighborhood public schools take everyone. Nevada should{" "}
                <span className="text-[#D4AF37]">never punish them for that</span>."
              </p>
              <p className="text-white/50 text-lg">Let's rebuild Nevada's public schools together.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Frequently Asked Questions</h2>
          </div>
          <div className="reveal space-y-3">
            {faqs.map((faq) => <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />)}
          </div>
        </div>
      </section>

      {/* ── SOURCES ── */}
      <section id="sources" className="relative py-16 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 text-center">Sources & Research</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {sources.map((s) => (
                <div key={s.title} className="bg-white/[0.04] border border-white/10 p-4">
                  <p className="text-white font-bold text-sm mb-1">{s.title}</p>
                  <p className="text-white/40 text-xs">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <PolicyCTA
        testId="education-cta"
        heading="Protect Nevada's Public Schools"
        subtitle="Every child deserves a great education. Every taxpayer deserves accountability. Let's build that together."
        primary={{ label: "Volunteer", to: "/volunteer" }}
        secondary={{ label: "Contact Us", to: "/contact" }}
        next={{ label: "Federal Funding", to: "/policies/federal-funding" }}
      />

      <RevealStyles />
    </div>
  );
};

export default EducationPage;
